import { useMeasures } from '../../../hooks/state/useMeasures'
import { useTypedSelector, useGLTF_GDPRCompliant } from '@k3/core'
import { useLoader } from '@react-three/fiber'
import { useEffect, useLayoutEffect, useMemo } from 'react'
import * as THREE from 'three'
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader'
import { TextureLoader } from 'three/src/loaders/TextureLoader'
import { useVerandaSelector } from '../../..'
import { TypedRootState } from '@k3/core/dist/src/lib/redux/reduxStore'
import { Helper } from '../../../classes/helper'

const FacadeMaterialName = 'Facade'
export const House = ({ ...props }): JSX.Element => {
  const individualHouse = useVerandaSelector(
    (state) => state.configuratorRights.individualHouse
  )

  const model = useTypedSelector((state: TypedRootState) => {
    const app = state?.frontend?.app || state?.configurator?.app || undefined
    const model = state?.metadata?.models?.find((m) => m.id === app?.modelId)
    return model
  })

  const { scene, materials } = useGLTF_GDPRCompliant(
    individualHouse
      ? model?.path ?? `model/house/Haus_V20.glb`
      : `model/house/Haus_V20.glb`,
    true
  ) as GLTF & any

  /**
   * !because we have 2 canvases for screenshotting, models need to be duplicated
   * https://github.com/pmndrs/react-three-fiber/issues/245
   */
  const clonedScene = useMemo(() => scene.clone(), [scene])

  const clonedMaterialFacade = useMemo(
    () => materials[FacadeMaterialName]?.clone(),
    [materials[FacadeMaterialName]]
  )

  const { facade, housRotate, startVerandaPosition } = useMeasures()

  let position: any = [0, 0, 0]
  let rotation: any = [0, 0, 0]

  const [colorMap, roughnessMap] = useLoader(TextureLoader, [
    'texture/house/Bricks051_1K_Color.jpg',
    'texture/house/Bricks051_1K_Roughness.jpg',
  ])
  const colorRoughnessMaps = [colorMap, roughnessMap]

  useLayoutEffect(() => {
    if (!clonedMaterialFacade) return
    if (facade.klinker) {
      setFassadeMaterial(colorRoughnessMaps)
      Object.assign(materials[FacadeMaterialName], {
        map: colorMap,
        envMapIntensity: 1,
        roughness: 1,
        color: new THREE.Color(0xa8a39e),
      })
    } else {
      Object.assign(materials[FacadeMaterialName], {
        roughnessMap: null,
        map: clonedMaterialFacade.map,
        color: clonedMaterialFacade.color,
      })
    }
  }, [facade, clonedMaterialFacade])
  useEffect(() => {
    clonedScene?.traverse(
      (obj: any) => {
        position = [0, 0, 0]
        rotation = [
          0,
          Helper.convertAngleToRadiant(parseFloat(housRotate as string)),
          0,
        ]

        clonedScene.traverse((obj: any) => {
          if (obj.isMesh) {
            obj.receiveShadow = !!obj?.userData?.receiveShadow
            obj.castShadow = !!obj?.userData?.castShadow
          }
        })

        if (!!clonedScene && model?.id) {
          clonedScene.userData.modelId = model.id
        }
      },
      [clonedScene, facade]
    )
  }, [clonedScene, housRotate])

  return (
    <group
      {...props}
      name={'not'}
      rotation={[
        0,
        Helper.convertAngleToRadiant(parseFloat(housRotate as string)),
        0,
      ]}
      position={startVerandaPosition}
    >
      <primitive object={clonedScene}></primitive>
    </group>
  )
}

const setFassadeMaterial = (map: any) => {
  map.forEach((m: any) => {
    m.wrapS = THREE.RepeatWrapping
    m.wrapT = THREE.RepeatWrapping
  })
}
